<template>
  <VcWidget v-if="accessoryItems.length > 0" class="order-2 lg:order-none">
    <div class="accessory-items flex flex-col justify-start">
      <div class="">
        <VcButton
          type="button"
          variant="link"
          size="lg"
          class="optional-fields-section !flex w-full items-center gap-2 text-base !font-bold !text-[--color-neutral-a1]"
          :disabled="isJapCurrency"
          @click="toggleSection()"
        >
          {{ $t("common.labels.accessory_items") }}
          <VcIcon
            v-if="!isJapCurrency"
            :name="isAccessoryItemsExpanded ? 'chevron-down' : 'chevron-up'"
            class="mr-2 stroke-white stroke-2 text-[--color-neutral-a1]"
            :size="24"
          />
        </VcButton>
      </div>
      <div
        v-show="!isAccessoryItemsExpanded"
        class="mb-1 mt-4 flex flex-col gap-5 border-t px-0 pt-4 md:mx-[-20px] md:px-5"
      >
        <template v-for="(item, index) in accessoryItems" :key="index">
          <div
            v-for="(accessoryId, accIndex) in getAccessoryIds(item)"
            :key="accIndex"
            class="flex items-center justify-between gap-3"
          >
            <span class="text-sm font-semibold leading-[22px]">
              {{ item.lineItem.product?.isConfigurable ? item.lineItem.product.code : item.lineItem?.sku }}
            </span>
            <VcSelect
              v-model="itemSelectedValues[`${index}-${accIndex}`]"
              :placeholder="getPlaceholder(item, accessoryId)"
              :disabled="isCustomerOrder"
              text-field="displayName"
              :items="getItems(item, accessoryId)"
              class="w-[250px]"
            >
            </VcSelect>
          </div>
        </template>
        <TlLoadingButton
          color="secondary"
          variant="outlineTL"
          class="disabled-button mt-3 h-[48px] w-full !py-3"
          size="lg"
          :complete-text="$t(`common.buttons.accessory_item_updated`)"
          :disabled="isButtonDisabled"
          @click="update"
        >
          {{ $t("common.buttons.update") }}
        </TlLoadingButton>
      </div>

      <div v-show="isAccessoryItemsExpanded" class="mt-2.5 font-medium leading-6 text-[--color-neutral-a2]">
        <span>{{ $t("common.labels.collapse_accessory_items") }}</span>
      </div>
    </div>
  </VcWidget>
</template>

<script setup lang="ts">
import { computed, ref, onMounted } from "vue";
import { useI18n } from "vue-i18n";
import { useCurrency, useDataAreaId } from "@/core/composables";
import { useChannelPartner } from "@/shared/account/composables/useChannelPartner";
import { getAccessoryDisplayName } from "@/shared/catalog/utils";
import { useCheckoutFacade } from "../composables/useCheckoutFacade";
import type { AccessoryType, CheckoutItemType } from "../types";
import type { AccessoryItemType } from "@/core/api/graphql/types";

const { checkoutItems, updateCartAccessories, isCustomerOrder } = useCheckoutFacade();

const { currentCurrency } = useCurrency();
const isJapCurrency = computed(() => {
  return currentCurrency.value.code === "JPY";
});

const isAccessoryItemsExpanded = ref(false);
const itemSelectedValues = ref<Record<string, AccessoryType>>({});
const originalSelectedValues = ref<Record<string, AccessoryType>>({}); // Store the original values for comparison

const isButtonDisabled = computed(() => {
  // Check if any value is different from its original value
  const hasChanges = Object.keys(itemSelectedValues.value).some((key) => {
    const current = itemSelectedValues.value[key];
    const original = originalSelectedValues.value[key];
    return current.accessoryId !== original?.accessoryId || current.lineItemId !== original?.lineItemId;
  });
  return !hasChanges; // Button is disabled only if there are no changes
});

const accessoryItems = computed(() => {
  const items = checkoutItems.value ?? [];

  return items.filter((item) => {
    const associations = item.lineItem?.product?.associationsTL;
    return associations && associations.length > 0;
  });
});

function getAccessoryIds(item: CheckoutItemType): string[] {
  if (!item.lineItem?.accessoryIdTL) {
    return [];
  }

  return item.lineItem.accessoryIdTL.split(",").map((id) => id.trim());
}

onMounted(() => {
  // Set initial expanded state based on currency
  isAccessoryItemsExpanded.value = isJapCurrency.value;

  accessoryItems.value.forEach((item, index) => {
    const selectedAccessoriesIds = getAccessoryIds(item);
    selectedAccessoriesIds.forEach((accessoryId, accIndex) => {
      const selectedValue = {
        lineItemId: item.lineItem?.id,
        accessoryId: accessoryId ?? "",
        accessoryName:
          item.lineItem?.product?.associationsTL?.find((association) => {
            return association.associatedObjectId === accessoryId;
          })?.product?.name ?? "",
      } as AccessoryType;

      itemSelectedValues.value[`${index}-${accIndex}`] = selectedValue;
      originalSelectedValues.value[`${index}-${accIndex}`] = { ...selectedValue }; // Deep copy the original value
    });
  });
});

function toggleSection() {
  // Don't allow toggling for Japanese currency
  if (isJapCurrency.value) {
    return;
  }

  isAccessoryItemsExpanded.value = !isAccessoryItemsExpanded.value;
}

function getAccessoryProduct(item: CheckoutItemType, accessoryId: string) {
  return item.lineItem?.product?.associationsTL?.find((association) => {
    return association.associatedObjectId === accessoryId;
  })?.product;
}

function getPlaceholder(item: CheckoutItemType, accessoryId: string) {
  const productAssociationName = getAccessoryProduct(item, accessoryId)?.name ?? "";

  if (!productAssociationName) {
    return "";
  }
  return getAccessoryDisplayName(productAssociationName, useI18n());
}

const { dataAreaId } = useDataAreaId();
const { isChannelPartner } = useChannelPartner();

function getItems(lineItem: CheckoutItemType, selectedAccessoryId: string) {
  const seen = new Map<string, { accessoryId: string; displayName: string; lineItemId: string | undefined }>();
  const selectedAccessoryProduct = getAccessoryProduct(lineItem, selectedAccessoryId);
  const selectedAccessoryFirstWord = selectedAccessoryProduct?.name?.split(/[\s-]/)[0] ?? "";

  const filteredAccessories = lineItem.lineItem.product!.associationsTL!.filter(
    (a) =>
      a.product?.name?.startsWith(selectedAccessoryFirstWord) &&
      a.product?.name?.toLowerCase()?.endsWith(`-${dataAreaId.value?.toLowerCase()}`),
  );

  // Channel partners are offered the US variant only — adapters and power cords (mirrors ProductAssociationHelper).
  const usVariants = filteredAccessories.filter((a) => a.product?.name?.toLowerCase().includes("-us"));
  const availableAccessories = isChannelPartner.value && usVariants.length > 0 ? usVariants : filteredAccessories;

  for (const association of availableAccessories) {
    const rawName = association.product?.name ?? "";
    const displayName = getAccessoryDisplayName(rawName, useI18n());
    const accessoryId = association.associatedObjectId;

    if (!accessoryId) {
      continue; // skip if accessoryId is undefined
    }

    if (
      currentCurrency.value.code === "CNY" &&
      association.product?.name?.toLowerCase().includes("adapter8") &&
      !association.product?.name?.toLowerCase().includes("-us")
    ) {
      continue; // Only ADAPTER8 US is available in PRC
    }

    const item = {
      accessoryId,
      displayName,
      lineItemId: lineItem?.lineItem.id,
    };

    const existing = seen.get(displayName);

    if (!existing || (!/-us/i.test(existing.displayName) && /-us/i.test(rawName))) {
      seen.set(displayName, item);
    }
  }

  // Convert Map values to array and sort alphabetically by name
  return Array.from(seen.values()).sort((a, b) => a.displayName.localeCompare(b.displayName));
}

const update = async (callback: () => void) => {
  try {
    if (itemSelectedValues.value) {
      const toUpdate = Object.values(itemSelectedValues.value).map(
        (item: AccessoryType) =>
          ({
            lineItemId: item.lineItemId,
            accessoryId: item.accessoryId,
          }) as AccessoryItemType,
      );

      await updateCartAccessories(toUpdate);

      // Update original values after successful update
      originalSelectedValues.value = Object.fromEntries(
        Object.entries(itemSelectedValues.value).map(([key, item]) => [key, { ...item }]),
      );
    }
  } finally {
    callback();
  }
};
</script>

<style lang="scss">
.checkout-page .disabled-button.vc-button--disabled {
  @apply bg-[--color-neutral-a5] cursor-not-allowed;
}
</style>
